import "#legacy/utilities/ordered_ajax.js";
import "#/shared/api/api-host-global.js";
import "#legacy/utilities/params.js";

window.AppointmentBooking ??= {};
window.AppointmentBooking.SlotPickerModal ??= {};
window.AppointmentBooking.SlotPickerModal.Helpers ??= {};

const VISIT_TYPES_URL = window.Sparkle.apiHost
  ? window.Sparkle.apiHost + window.SparkleConfig.routes.embed.filtersPhysiciansVisitTypes
  : undefined;

// Whether `visitType` is the current selection. An empty selection means "any visit
// type" and matches no option.
function isSelected(visitType, selectedValue) {
  return selectedValue != null && selectedValue !== "" && String(visitType.value) === String(selectedValue);
}

// Provides the visit type filter options for the slot picker modal header's visit
// type select: fetches the full option list and narrows it to the options available
// for the modal's resource (scope).
AppointmentBooking.SlotPickerModal.Helpers.VisitTypeOptions = class VisitTypeOptions {
  // onChange is called whenever the available or scoped visit types change, so the
  // owner can push a fresh item list into its select.
  constructor({ onChange } = {}) {
    this.setScope = this.setScope.bind(this);
    this.optionsFor = this.optionsFor.bind(this);
    this.offerableVisitTypes = this.offerableVisitTypes.bind(this);
    this.loadAvailableVisitTypes = this.loadAvailableVisitTypes.bind(this);
    this.applyVisitTypeScope = this.applyVisitTypeScope.bind(this);

    this.onChange = onChange;
    this.availableVisitTypes = [];
    this.scopedVisitTypes = null;

    this.loadAvailableVisitTypes();
  }

  // PhysicianSearch scope to apply so we only offer visit types matching a particular search
  setScope(scope) {
    // Lazy retry after a failed initial fetch: the memo is only null after a
    // failure, so an in-flight first fetch doesn't get a second .then attached
    if (_.isEmpty(this.availableVisitTypes) && !this.constructor.allVisitTypesFetch) {
      this.loadAvailableVisitTypes();
    }
    scope = Sparkle.Utilities.sanitizeParams(scope);
    const requestId = (this.scopeRequestId = (this.scopeRequestId || 0) + 1);

    if (_.isEmpty(scope)) {
      this.scopedVisitTypes = null;
      this.onChange?.();
    } else {
      Sparkle.Utilities.OrderedAJAX.getJSON("AppointmentBooking.SlotPickerModal.VisitTypeOptions", VISIT_TYPES_URL, {
        scope,
      }).then((data) => {
        if (requestId !== this.scopeRequestId) {
          return;
        }

        this.scopedVisitTypes = data.visit_types;
        this.onChange?.();
      });
    }
  }

  // Returns the options to offer, given the currently selected value: scoped and
  // visible (or currently selected). Parent categories arrive before their children
  // (API order), which the header view relies on when nesting.
  optionsFor(selectedValue) {
    let visitTypes = JSON.parse(JSON.stringify(this.offerableVisitTypes())); // Dup so we don't modify raw data

    // Mark all visit types that have children so scoping can tell parents apart
    const parents = _.compact(_.pluck(visitTypes, "parent"));
    _.each(visitTypes, (visitType) => {
      visitType.hasChildren = _.indexOf(parents, visitType.value) > -1;
    });

    if (this.scopedVisitTypes != null) {
      visitTypes = this.applyVisitTypeScope(visitTypes, selectedValue);
    }

    return _.filter(visitTypes, (visitType) => visitType.visible || isSelected(visitType, selectedValue));
  }

  // HELPER METHODS

  // Returns the available options that carry visit type ids. An option's `value` holds
  // the ids it filters by, and the filters endpoint sends an empty value for a hidden
  // visit type, so that option could filter nothing.
  offerableVisitTypes() {
    return _.filter(this.availableVisitTypes, (visitType) => visitType.value !== "");
  }

  // Fetches (memoized across instances) and stores the unscoped option list.
  // The memo is cleared on failure so the next call retries rather than
  // replaying the rejection.
  loadAvailableVisitTypes() {
    const fetch = (this.constructor.allVisitTypesFetch ??= jQuery.getJSON(VISIT_TYPES_URL).fail(() => {
      this.constructor.allVisitTypesFetch = null;
    }));

    return fetch.then((data) => {
      this.availableVisitTypes = data.visit_types;
      this.onChange?.();
    });
  }

  // Reduce the list to the visit types in the scoped list: the selected option stays,
  // children stay when they appear in the scoped list, parents stay when any of their
  // children stayed.
  applyVisitTypeScope(visitTypes, selectedValue) {
    const selectedVisitType = _.find(visitTypes, (visitType) => isSelected(visitType, selectedValue));

    const [parentVisitTypes, leafVisitTypes] = _.partition(visitTypes, { hasChildren: true });

    // Mark all disabled unless it is the selected one
    _.each(visitTypes, (visitType) => {
      visitType.disabled = selectedVisitType ? visitType.value !== selectedVisitType.value : true;
    });

    // Enable leaf visit types that appear in the scoped list.
    _.each(this.scopedVisitTypes, (scopedVisitType) => {
      const visitType = this.findUnscopedMatch(leafVisitTypes, scopedVisitType);
      if (!visitType) {
        return;
      }
      visitType.disabled = false;
    });

    // Enable parents that have enabled children
    _.each(parentVisitTypes, (visitType) => {
      if (_.findWhere(visitTypes, { disabled: false, parent: visitType.value })) {
        visitType.disabled = false;
      }
    });

    return _.reject(visitTypes, { disabled: true });
  }

  // Finds an unscoped visit type that corresponds to the given scoped visit type
  // NOTE: We can't match directly on value, because the scope values may not include all the same visit type ids, therefore we
  // look for one that contains all of the scoped visit type ids, in the shortest list of ids
  findUnscopedMatch(visitTypes, scopedVisitType) {
    const scopedIds = scopedVisitType.value.split(",");
    let result = _.filter(
      visitTypes,
      (visitType) => _.intersection(visitType.value.split(","), scopedIds).length === scopedIds.length,
    );
    result = _.sortBy(result, (visitType) => visitType.value.split(",").length);
    return result[0];
  }
};
